import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import type { ChatResolveEntityOption } from '../../models/admin/AdminChatResolveDraft';
import ChatResolveDraftActionEditor from './ChatResolveDraftActionEditor';
import ChatResolveDraftNoteEditor from './ChatResolveDraftNoteEditor';
import { useChatResolveDraftsDialog } from './useChatResolveDraftsDialog';

export interface ChatResolveDraftsDialogProps {
  open: boolean;
  conversationId: string | null;
  entityOptions: ChatResolveEntityOption[];
  onClose: () => void;
  onSaved?: (counts: { notes: number; actions: number }) => void;
}

const ChatResolveDraftsDialog: React.FC<ChatResolveDraftsDialogProps> = ({
  open,
  conversationId,
  entityOptions,
  onClose,
  onSaved,
}) => {
  const {
    summary,
    notes,
    setNotes,
    actions,
    setActions,
    attachToOptions,
    draftsGenerated,
    isGenerating,
    isSaving,
    error,
    selectedCounts,
    generateDrafts,
    updateNoteEntity,
    updateActionEntity,
    handleSave,
  } = useChatResolveDraftsDialog({
    open,
    conversationId,
    entityOptions,
    onClose,
    onSaved,
  });

  const handleNoteSelectedChange = useCallback(
    (draftId: string, selected: boolean) => {
      setNotes((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, selected } : item)),
      );
    },
    [setNotes],
  );

  const handleNoteContentChange = useCallback(
    (draftId: string, content: string) => {
      setNotes((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, content } : item)),
      );
    },
    [setNotes],
  );

  const handleActionSelectedChange = useCallback(
    (draftId: string, selected: boolean) => {
      setActions((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, selected } : item)),
      );
    },
    [setActions],
  );

  const handleActionTitleChange = useCallback(
    (draftId: string, title: string) => {
      setActions((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, title } : item)),
      );
    },
    [setActions],
  );

  const handleActionDescriptionChange = useCallback(
    (draftId: string, description: string) => {
      setActions((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, description } : item)),
      );
    },
    [setActions],
  );

  const handleActionDueAtChange = useCallback(
    (draftId: string, dueAt: string) => {
      setActions((current) =>
        current.map((item) => (item.draftId === draftId ? { ...item, dueAt } : item)),
      );
    },
    [setActions],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Draft notes from chat
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isGenerating ? (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, justifyContent: 'center' }}
          >
            <CircularProgress size={24} />
            <Typography color="text.secondary">Analysing chat…</Typography>
          </Box>
        ) : (
          <>
            {summary && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {summary}
              </Typography>
            )}

            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {!draftsGenerated && !error && (
              <Typography variant="body2" color="text.secondary">
                Optionally analyse this chat to propose draft CRM notes and follow-up actions.
                Nothing is saved until you review and approve.
              </Typography>
            )}

            {draftsGenerated && !notes.length && !actions.length && !error && (
              <Typography variant="body2" color="text.secondary">
                No draft notes or actions were suggested for this chat.
              </Typography>
            )}

            {notes.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Notes
                </Typography>
                {notes.map((note) => (
                  <ChatResolveDraftNoteEditor
                    key={note.draftId}
                    note={note}
                    attachToOptions={attachToOptions}
                    onSelectedChange={handleNoteSelectedChange}
                    onContentChange={handleNoteContentChange}
                    onEntityChange={updateNoteEntity}
                  />
                ))}
              </Box>
            )}

            {actions.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Actions
                </Typography>
                {actions.map((action) => (
                  <ChatResolveDraftActionEditor
                    key={action.draftId}
                    action={action}
                    attachToOptions={attachToOptions}
                    onSelectedChange={handleActionSelectedChange}
                    onTitleChange={handleActionTitleChange}
                    onDescriptionChange={handleActionDescriptionChange}
                    onEntityChange={updateActionEntity}
                    onDueAtChange={handleActionDueAtChange}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={isSaving || isGenerating}>
          Cancel
        </Button>
        {!draftsGenerated && (
          <Button
            variant="contained"
            onClick={() => void generateDrafts()}
            disabled={isGenerating || !conversationId}
          >
            {isGenerating ? <CircularProgress size={18} color="inherit" /> : 'Generate drafts'}
          </Button>
        )}
        {draftsGenerated && (
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={
              isGenerating || isSaving || selectedCounts.notes + selectedCounts.actions === 0
            }
          >
            {isSaving ? <CircularProgress size={18} color="inherit" /> : 'Save selected'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChatResolveDraftsDialog;
